import {
  type BackendChoice,
  backendRoutingHeaders,
} from '@electron/shared/backend-choice.types';
import { isInternalFeatureAccess } from '@electron/shared/utils';
import { setUserHeaders } from '@littlebird/auth-client';
import { differenceInDays } from 'date-fns/differenceInDays';
import { action, computed, makeObservable, observable, reaction } from 'mobx';
import { makePersistable, stopPersisting } from 'mobx-persist-store';

import { electronApis } from '@/features/electron-api';
import { queryClient } from '@/query/client';
import {
  type User,
  USER_QUERY_KEY,
  userMutationOptions,
  userQueryOptions,
} from '@/query/user';
import { inElectron } from '@/utils/capabilities';

interface SyncUpdateOptions {
  throwOnError?: boolean;
}

const TIMEZONELESS_TIMESTAMP =
  /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}(\.\d+)?$/;

const parseUtcTimestamp = (value: string | null | undefined): number | null => {
  if (!value) return null;
  const normalized = TIMEZONELESS_TIMESTAMP.test(value)
    ? `${value.replace(' ', 'T')}Z`
    : value;
  const timestampMs = new Date(normalized).getTime();
  return Number.isNaN(timestampMs) ? null : timestampMs;
};

// Counts backend choices applied from a switch or from main. A profile answer
// to a request sent before the latest one keeps that newer choice. Counters
// only grow, so they need no reset between sessions
let backendChoiceGeneration = 0;
// The generation when the store last sent a profile request of its own
let profileRequestGeneration = 0;
// The choice applied last. It outlives a window with no user yet, so a profile
// answer that lands after it can still take it
let latestAppliedChoice: BackendChoice = 'python';

const fetchLatestUser = () =>
  queryClient.fetchQuery({ ...userQueryOptions(), staleTime: 0 });

export class UserStore {
  user: User | null = null;
  hideInternalTools = false;
  private lastKnownServerState: User | null = null; // used for rollback on mutation errors
  private stopSideEffects: (() => void) | null = null;

  constructor() {
    makeObservable(this, {
      user: observable,
      isAuthenticated: computed,
      userId: computed,
      featureAccess: computed,
      hasInternalAccess: computed,
      isInternalUser: computed,
      hideInternalTools: observable,
      accountCreatedAtMs: computed,
      hasSignedBaa: computed,
      calendarConnected: computed,
      backendChoice: computed,
      userHeader: computed,
      isOnboarded: computed,
      needsWebOnboarding: computed,
      setUser: action,
      updateUser: action,
      rollback: action,
      clear: action,
      setHideInternalTools: action,
      applyBackendChoice: action,
    });
  }

  /**
   * Reads the user saved in local storage, then saves every change and keeps
   * the API client's user headers current. Idempotent; returns a disposer
   */
  start(): () => void {
    if (this.stopSideEffects) return this.stopSideEffects;

    let stopped = false;
    let stopHeaders: (() => void) | undefined;
    let stopBackendChoice: (() => void) | undefined;
    let stopMainChoice: (() => void) | undefined;
    void makePersistable(this, {
      name: 'user',
      properties: ['user', 'hideInternalTools'],
      storage: window.localStorage,
    }).then(() => {
      if (stopped) return;
      setUserHeaders(this.userHeader);

      stopHeaders = reaction(
        () => this.userHeader,
        (headers) => setUserHeaders(headers)
      );
      // Main and ContextKit send their own backend requests and open the
      // socket, so they route by the same cached profile
      if (!inElectron()) return;
      // What main last heard or announced. A choice main announced is not
      // sent back, or two quick opposite changes would echo between windows
      let mainChoice: BackendChoice | undefined;
      stopMainChoice = electronApis.system.listenForBackendChoiceChange(
        (choice) => {
          mainChoice = choice;
          this.applyBackendChoice(choice);
        }
      );
      // Main may have restored a choice an earlier launch saved, and this
      // window's cached profile is no newer. So the window sends only the
      // changes that come after, such as a profile answer or a switch
      void electronApis.system
        .getBackendChoice()
        .catch((error) => {
          console.error('Failed to read backend choice from main:', error);
          return undefined;
        })
        .then((restoredChoice) => {
          if (stopped) return;
          mainChoice ??= restoredChoice;
          stopBackendChoice = reaction(
            // A window with no profile has no choice of its own to send, and
            // a profile arriving later is one
            () => (this.user ? this.backendChoice : undefined),
            (choice) => {
              if (choice === undefined || choice === mainChoice) return;
              mainChoice = choice;
              electronApis.system
                .setBackendChoice(choice)
                .catch((error) =>
                  console.error('Failed to send backend choice to main:', error)
                );
            }
          );
        });
    });

    const stop = () => {
      if (stopped) return;
      stopped = true;
      stopHeaders?.();
      stopBackendChoice?.();
      stopMainChoice?.();
      stopPersisting(this);
      if (this.stopSideEffects === stop) this.stopSideEffects = null;
    };
    this.stopSideEffects = stop;
    return stop;
  }

  get isAuthenticated(): boolean {
    return this.user !== null;
  }

  get userId(): string | undefined {
    return this.user?.id;
  }

  get featureAccess(): string | undefined {
    return this.user?.feature_access;
  }

  /** The account's real internal access, whatever the user chose to show */
  get hasInternalAccess(): boolean {
    return isInternalFeatureAccess(this.user?.feature_access);
  }

  /**
   * Whether internal-only UI shows. False while an internal user hides
   * internal tools, for demos, so every check reads like a customer's.
   * React hooks and memoized code read it through `useIsInternalUser()`
   */
  get isInternalUser(): boolean {
    return this.hasInternalAccess && !this.hideInternalTools;
  }

  get accountCreatedAtMs(): number | null {
    return parseUtcTimestamp(this.user?.created_at);
  }

  // Not a computed: it reads the wall clock, which MobX cannot invalidate on
  get accountAgeDays(): number | null {
    const createdAtMs = this.accountCreatedAtMs;
    return createdAtMs === null
      ? null
      : differenceInDays(Date.now(), createdAtMs);
  }

  get hasSignedBaa(): boolean {
    return Boolean(this.user?.feature_flags?.has_signed_baa);
  }

  get calendarConnected(): boolean {
    return Boolean(this.user?.calendar_connected);
  }

  /** Python when there is no user, or a user saved before profiles carried the choice */
  get backendChoice(): BackendChoice {
    return this.user?.backend_choice ?? 'python';
  }

  /** Every request to our backend sends these; the API clients read them at request time */
  get userHeader(): Record<string, string> {
    if (!this.user) return {};
    return {
      'x-user-id': this.user.id,
      'x-internal-user': String(this.isInternalUser),
      ...backendRoutingHeaders(this.backendChoice),
    };
  }

  get isOnboarded(): boolean {
    if (typeof this.user?.is_onboarded_desktop === 'boolean') {
      return this.user.is_onboarded_desktop;
    }
    return Boolean(this.user?.name?.trim());
  }

  /**
   * Web onboarding targets genuinely new users: anyone who has completed
   * onboarding elsewhere (desktop flag or the legacy name fallback) skips it.
   */
  get needsWebOnboarding(): boolean {
    if (!this.user) return false;
    return !this.isOnboarded && this.user.is_onboarded_web !== true;
  }

  setUser(user: User | null): void {
    this.user = user;
    this.lastKnownServerState = user;
  }

  /**
   * Applies a change to the user. The cached user no longer matches, so the
   * next refresh fetches it again instead of putting back the old values.
   */
  updateUser(payload: Partial<User>): void {
    if (this.user) {
      this.user = { ...this.user, ...payload };
    }
    void queryClient.invalidateQueries({
      queryKey: USER_QUERY_KEY,
      refetchType: 'none',
    });
  }

  rollback(): void {
    this.user = this.lastKnownServerState;
  }

  clear(): void {
    this.user = null;
    this.lastKnownServerState = null;
    this.hideInternalTools = false;
  }

  setHideInternalTools(hide: boolean): void {
    this.hideInternalTools = hide;
  }

  /** Stays the same until another choice is applied; see `applyBackendChoice` */
  backendChoiceGenerationNow(): number {
    return backendChoiceGeneration;
  }

  /**
   * Routes this window by a newer choice: a saved switch, the server's answer
   * or main's announcement of a choice made elsewhere. An unchanged choice
   * does nothing, so the reaction that tells main does not echo it back. Pass
   * `requestedAtGeneration` for an answer to a request, which loses to any
   * choice applied while it was out
   */
  applyBackendChoice(
    choice: BackendChoice,
    requestedAtGeneration?: number
  ): void {
    if (
      requestedAtGeneration !== undefined &&
      requestedAtGeneration !== backendChoiceGeneration
    ) {
      return;
    }
    backendChoiceGeneration += 1;
    latestAppliedChoice = choice;
    if (!this.user || this.backendChoice === choice) return;
    this.user = { ...this.user, backend_choice: choice };
    // The server holds this choice already, so a failed edit keeps it
    if (this.lastKnownServerState) {
      this.lastKnownServerState = {
        ...this.lastKnownServerState,
        backend_choice: choice,
      };
    }
    void queryClient.invalidateQueries({
      queryKey: USER_QUERY_KEY,
      refetchType: 'none',
    });
  }

  /**
   * Updates user with server sync. Handles optimistic updates and mutation-only fields.
   * @param payload - User fields to update, plus optional mutation-only fields like reset_todo_filter_to_defaults
   */
  async syncUpdate(
    payload: Partial<User> & { reset_todo_filter_to_defaults?: boolean },
    options?: SyncUpdateOptions
  ): Promise<void> {
    const { reset_todo_filter_to_defaults: _, ...userFields } = payload;
    this.updateUser(userFields);

    try {
      const mutationFn = userMutationOptions().mutationFn;
      if (mutationFn) {
        await mutationFn(payload);
      }

      const updatedUser =
        await this.fetchUserKeepingNewerChoice(fetchLatestUser);
      this.setUser(updatedUser);
    } catch (error) {
      console.error('User mutation failed:', error);
      this.rollback();
      if (options?.throwOnError) {
        throw error;
      }
    }
  }

  /**
   * Refreshes user data from the server. Call it after a change the cached
   * user cannot know about yet.
   */
  async refresh(): Promise<void> {
    await this.loadUser(fetchLatestUser);
  }

  /**
   * Refreshes user data from the server unless the cached user is still fresh.
   */
  async refreshIfStale(): Promise<void> {
    await this.loadUser(() => queryClient.fetchQuery(userQueryOptions()));
  }

  private async fetchUserKeepingNewerChoice(
    fetchUser: () => Promise<User>
  ): Promise<User> {
    // A call made while a profile request is on its way joins that request,
    // and gets an answer that may predate the latest choice
    const isJoining = queryClient.isFetching({ queryKey: USER_QUERY_KEY }) > 0;
    if (!isJoining) profileRequestGeneration = backendChoiceGeneration;
    const generation = profileRequestGeneration;
    const user = await fetchUser();
    if (generation === backendChoiceGeneration) return user;
    const repairedUser = { ...user, backend_choice: latestAppliedChoice };
    // A later read of the cached answer, such as `refreshIfStale`, must not
    // bring the older choice back
    queryClient.setQueryData(USER_QUERY_KEY, repairedUser);
    return repairedUser;
  }

  private async loadUser(fetchUser: () => Promise<User>): Promise<void> {
    try {
      this.setUser(await this.fetchUserKeepingNewerChoice(fetchUser));
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  }
}

export const userStore = new UserStore();
// Every window starts persistence when this module loads, as before start()
// existed; a test stops it with the disposer and starts its own instance
userStore.start();
